import { DateTime } from 'luxon';

import { ColorName } from '@common/services/colors.service';
import { OpenPopinOriginator } from '@common/enum/open-popin-originator.enum';

import { HostFlatNode, ServiceFlatNode } from './models';

export enum HostStatus {
    Ok = 1,
    Warning = 2,
    Critical = 3,
    CriticalAcknowledged = 4,
    Unknown = 5,
    Undefined = 6
}

export enum ServiceStatus {
    Ok = 1,
    Warning = 2,
    Critical = 3,
    CriticalAcknowledged = 4,
    Unknown = 5,
    Undefined = 6
}

export class PrtgConstants {
    public static Application: string = 'PrtgApi';
    public static BaseUrl = 'monitoring';
    public static BaseApi = 'prtg';
    public static Hosts = 'hosts';
    public static Services = 'services';
    public static Details = 'details';
    public static DefaultUrl: string = `/${PrtgConstants.BaseUrl}/${PrtgConstants.Hosts}`;
    public static AverageSecs: number = 60 * 60;
    public static Filter: string = 'filter';
    public static BeginningOfTime = DateTime.utc(2024).startOf('year');
    public static DataLifeTime = 7;

    public static GraphicColors = new Map<string, ColorName>(
        [
            ['Ok', ColorName.Success],
            ['Warning', ColorName.Warning1],
            ['Critical', ColorName.Danger],
            ['CriticalAcknowledged', ColorName.Warning1],
            ['Unknown', ColorName.Blue],
            ['Undefined', ColorName.Grey]
        ]
    );

    public static HostUpFlagStatus: number =
        HostStatus.Ok
        | HostStatus.Unknown;
    public static HostDownFlagStatus: number =
        HostStatus.Critical
        | HostStatus.CriticalAcknowledged
        | HostStatus.Warning
        | HostStatus.Undefined;

    public static HostUpFlagStatusArray = [HostStatus.Ok, HostStatus.Unknown];
    public static HostDownFlagStatusArray = [HostStatus.Critical, HostStatus.CriticalAcknowledged, HostStatus.Warning, HostStatus.Undefined];
    public static HostFullFlagStatus = Object.keys(HostStatus)
        .map((key: any) =>
            parseInt(HostStatus[key], 10)
        )
        .filter((id: number) =>
            !isNaN(id)
        )
        .reduce((acc: number, cur: number) =>
            // tslint:disable-next-line: no-bitwise
            acc | cur, 0
        );
    public static HostFullFlagStatusArray = Object.keys(HostStatus)
        .map((key: any) =>
            parseInt(HostStatus[key], 10)
        )
        .filter((id: number) =>
            !isNaN(id)
        )
        .reduce((acc: number[], cur: number) =>
            [...acc, cur], []
        );

    public static ServiceUpFlagStatus: number =
        ServiceStatus.Ok
        | ServiceStatus.Unknown;
    public static ServiceDownFlagStatus: number =
        ServiceStatus.Critical
        | ServiceStatus.CriticalAcknowledged
        | ServiceStatus.Warning
        | ServiceStatus.Undefined;

    public static ServiceUpFlagStatusArray = [ServiceStatus.Ok, ServiceStatus.Unknown];
    public static ServiceDownFlagStatusArray = [ServiceStatus.Critical, ServiceStatus.CriticalAcknowledged, ServiceStatus.Warning, ServiceStatus.Undefined];
    public static ServiceFullFlagStatus = Object.keys(ServiceStatus)
        .map((key: any) =>
            parseInt(ServiceStatus[key], 10)
        )
        .filter((id: number) =>
            !isNaN(id)
        )
        .reduce((acc: number, cur: number) =>
            // tslint:disable-next-line: no-bitwise
            acc | cur, 0
        );
    public static ServiceFullFlagStatusArray = Object.keys(ServiceStatus)
        .map((key: any) =>
            parseInt(ServiceStatus[key], 10)
        )
        .filter((id: number) =>
            !isNaN(id)
        )
        .reduce((acc: number[], cur: number) =>
            [...acc, cur], []
        );

    public static MinZoomLevel = 0.25;
    public static MaxZoomLevel = 7;
    public static InitZoomLevel = 1;
    public static ZoomStep = 0.5;
    public static hostNodesQSFields: Array<keyof HostFlatNode> = ['status', 'hostId', 'address', 'displayName', 'typeHost', 'lastValue'];
    public static serviceNodesQSFields: Array<keyof ServiceFlatNode> = ['status', 'hostName', 'hostAddress', 'serviceId', 'displayName', 'typeService', 'lastValue'];

    public static getHostFullFlagStatusArray(): number[] {
        return Object.keys(HostStatus)
            .map((key: any) =>
                parseInt(HostStatus[key], 10)
            )
            .filter((id: number) =>
                !isNaN(id)
            )
            .reduce((acc: number[], cur: number) =>
                [...acc, cur], []
            );
    }

    public static getServiceFullFlagStatusArray(): number[] {
        return Object.keys(ServiceStatus)
            .map((key: any) =>
                parseInt(ServiceStatus[key], 10)
            )
            .filter((id: number) =>
                !isNaN(id)
            )
            .reduce((acc: number[], cur: number) =>
                [...acc, cur], []
            );
    }

    public static ToHostsRoute = () =>
        [
            PrtgConstants.BaseUrl,
            PrtgConstants.Hosts
        ];

    public static ToHostsDetailsRoute = (hostId: string, page: number = 1, limit: number = 100) =>
        [
            PrtgConstants.BaseUrl,
            PrtgConstants.Hosts,
            { outlets: { [PrtgConstants.Details]: [PrtgConstants.Details, OpenPopinOriginator.PRTG_HOSTS, hostId, page.toString(), limit.toString()] } }
        ];

    public static ToHostsRouteWithParams = (page: number = 1, limit: number = 100) =>
        [
            PrtgConstants.BaseUrl,
            PrtgConstants.Hosts,
            { queryParams: { page, limit } }
        ];

    public static ToServicesRoute = () =>
        [
            PrtgConstants.BaseUrl,
            PrtgConstants.Services
        ];

    public static ToServicesDetailsRoute = (serviceId: string, page: number = 1, limit: number = 100) =>
        [
            PrtgConstants.BaseUrl,
            PrtgConstants.Services,
            {
                outlets: {
                    [PrtgConstants.Details]: [PrtgConstants.Details, OpenPopinOriginator.PRTG_SERVICES.toString(), serviceId, page.toString(), limit.toString()]
                }
            }
        ];
}
